import { BUTTON_VARIANTS, IconButton, SecondaryButton, GhostButton } from 'ui/button';
import { ord, string } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { map, filter, sort } from 'fp-ts/Array';
import { ListUnsubscribe } from 'ui/icons/events/ListUnsubscribe';
import { Add16 } from 'ui/icons/action/Add16';
import { Tooltip } from 'ui/tooltip/Tooltip';
import { H4, Body2, RouterLink2 } from 'ui/theme/typography';
import { Member, Project } from '../../types';
import { useOrganizationMembersQueryContext } from '../../contexts/OrganizationMembersQueryContext';
import {
  MODAL_NAMES,
  useOrganizationModalsContext,
} from '../../contexts/OrganizationModalsContext';
import {
  Wrapper,
  Header,
  ProjectRow,
  Body2NoMargin,
  InlineFlexRow,
} from './ProjectMembership.styled';

const byName = pipe(
  string.Ord,
  ord.contramap((project: Project) => project.name),
);

export const ProjectMembership = ({ member }: { member: Member }) => {
  const { showModal } = useOrganizationModalsContext();
  const { projects, projectsById, rolesById } = useOrganizationMembersQueryContext();

  const { projectMemberships } = member;
  const roleIdsByProjectId = projectMemberships.reduce(
    (acc, { projectId, roleId }) => ({
      ...acc,
      [projectId]: roleId,
    }),
    {} as Record<number, number>,
  );

  const memberProjects = pipe(
    projectMemberships,
    map((pm) => projectsById[pm.projectId]),
    sort(byName),
  );

  const memberProjectIds = memberProjects.map(({ id }) => id);

  const nonMemberProjects = pipe(
    projects,
    filter(({ id }) => !memberProjectIds.includes(id)),
    sort(byName),
  );

  return (
    <>
      <Wrapper>
        <Header>
          <H4>Project membership</H4>
          <Body2>
            Member of {projectMemberships.length} of {projects.length} projects
          </Body2>
        </Header>
        {memberProjects.length > 0 &&
          memberProjects.map((project: Project) => {
            const { id, name } = project;
            const role = rolesById[roleIdsByProjectId[id]];
            return (
              <ProjectRow key={id} data-test={`project-membership-row-${id}`}>
                <RouterLink2 to={`/projects/${id}`}>{name}</RouterLink2>
                <Body2NoMargin>{role?.name}</Body2NoMargin>
                <InlineFlexRow>
                  <SecondaryButton
                    onClick={() => {
                      showModal({
                        name: MODAL_NAMES.MEMBERSHIP_ROLE,
                        payload: {
                          member,
                          project,
                          role,
                        },
                      });
                    }}
                  >
                    Manage
                  </SecondaryButton>
                  <Tooltip tooltipContent="Remove from project">
                    <IconButton
                      variant={BUTTON_VARIANTS.SECONDARY}
                      onClick={() => {
                        showModal({
                          name: MODAL_NAMES.REMOVE_FROM_PROJECT,
                          payload: {
                            member,
                            project,
                          },
                        });
                      }}
                    >
                      <ListUnsubscribe />
                    </IconButton>
                  </Tooltip>
                </InlineFlexRow>
              </ProjectRow>
            );
          })}
        {nonMemberProjects.length > 0 &&
          nonMemberProjects.map((project: Project) => {
            const { id, name } = project;
            return (
              <ProjectRow key={id} data-test={`project-membership-row-${id}`}>
                <RouterLink2 to={`/projects/${id}`}>{name}</RouterLink2>
                <div />
                <div>
                  <GhostButton
                    onClick={() => {
                      showModal({
                        name: MODAL_NAMES.ADD_TO_PROJECT,
                        payload: {
                          member,
                          project,
                        },
                      });
                    }}
                  >
                    <Add16 />
                    Add to project
                  </GhostButton>
                </div>
              </ProjectRow>
            );
          })}
      </Wrapper>
    </>
  );
};
